import * as React from "react";

import { motion } from "framer-motion";
import { AlertCircle, Info, LucideIcon, ShieldCheck } from "lucide-react";

import { Button } from "./button";
import { cn } from "../../../lib/utils";
import { Dialog, DialogContent } from "./dialog";
import { isAndroidAppWebView } from "../../helpers/utils";
import {
  TAILWIND_BUTTON_BASE_CLASS,
  TAILWIND_SUCCESS_BUTTON_RING_CLASS,
} from "../../helpers/constants";

export type StatusModalVariant = "success" | "error" | "warning" | "info";

export interface StatusModalMessage {
  /** Main text (can include JSX, e.g. with highlighted spans) */
  content: React.ReactNode;
  /** Optional subheading above this block */
  heading?: string;
  /** Optional className for this message block */
  className?: string;
}

export interface StatusModalProps {
  /** Controlled open state */
  open: boolean;
  /** Called when open state should change (e.g. close button, overlay) */
  onOpenChange: (open: boolean) => void;
  /** Modal variant: success, error, warning, info */
  variant?: StatusModalVariant;
  /** Title below the icon */
  title: string;
  /** Message blocks. Pass array of { content, heading?, className? } or a single ReactNode for one block. */
  message?: React.ReactNode | StatusModalMessage[];
  /** Primary action button label */
  primaryButtonLabel?: string;
  /** Primary action button click handler. If not provided, button is not rendered. */
  onPrimaryAction?: () => void;
  /** Optional secondary button */
  secondaryButtonLabel?: string;
  /** Optional secondary button click handler */
  onSecondaryAction?: () => void;
  /** Optional footer content (e.g. "Have questions? Contact support") */
  footer?: React.ReactNode;
  /** Optional custom actions (buttons) rendered with the message block, above the footer */
  customActions?: React.ReactNode;
  /** Optional custom icon (overrides variant icon) */
  icon?: LucideIcon;
  /** Optional class for the content container */
  className?: string;
  /** Optional size hint (matches MUI Dialog maxWidth semantics) */
  width?: "sm" | "md" | "lg";
  /** Optional max width class for dialog (default sm:max-w-lg) */
  maxWidthClassName?: string;
  /** Optional class for the primary button (e.g. gradient styles) */
  primaryButtonClassName?: string;
  /** When true, hide the close (X) button and prevent closing via overlay/escape (only primary/secondary action can close) */
  forcePrimaryActionOnly?: boolean;
}

const variantConfig: Record<
  StatusModalVariant,
  { icon: LucideIcon; iconWrapperClass: string; iconInnerClass: string }
> = {
  success: {
    icon: ShieldCheck,
    iconWrapperClass: "from-secondary/20 to-cyan-400/20",
    iconInnerClass: "from-secondary to-cyan-400",
  },
  error: {
    icon: AlertCircle,
    iconWrapperClass: "from-destructive/20 to-red-500/20",
    iconInnerClass: "from-destructive to-red-500",
  },
  warning: {
    icon: AlertCircle,
    iconWrapperClass: "from-amber-500/20 to-orange-500/20",
    iconInnerClass: "from-amber-500 to-orange-500",
  },
  info: {
    icon: Info,
    iconWrapperClass: "from-secondary/20 to-cyan-400/20",
    iconInnerClass: "from-secondary to-cyan-400",
  },
};

function renderMessage(message: StatusModalProps["message"]): React.ReactNode {
  if (message == null) return null;
  if (Array.isArray(message)) {
    return (
      <>
        {message.map((block, index) => (
          <div
            key={index}
            className={cn(
              "text-left bg-muted/30 rounded-xl p-6 mb-4 last:mb-0",
              block.className,
            )}
          >
            {block.heading && (
              <h3 className="font-semibold text-foreground mb-2">
                {block.heading}
              </h3>
            )}
            <div className="text-muted-foreground [&>p]:mb-4 [&>p:last-child]:mb-0">
              {block.content}
            </div>
          </div>
        ))}
      </>
    );
  }
  return (
    <div className="text-center rounded-xl p-6">
      <div className="text-foreground [&>p]:mb-4 [&>p:last-child]:mb-0">
        {message}
      </div>
    </div>
  );
}

const StatusModal: React.FC<StatusModalProps> = ({
  open,
  onOpenChange,
  variant,
  title,
  message,
  primaryButtonLabel,
  onPrimaryAction,
  secondaryButtonLabel,
  onSecondaryAction,
  footer,
  customActions,
  icon: CustomIcon,
  className,
  width,
  maxWidthClassName = "sm:max-w-lg",
  primaryButtonClassName,
  forcePrimaryActionOnly = false,
}) => {
  const config = variant ? variantConfig[variant] : undefined;
  const IconComponent = CustomIcon ?? config?.icon;
  const isAndroidApp = isAndroidAppWebView();

  const resolvedMaxWidthClassName = React.useMemo(() => {
    if (maxWidthClassName !== "sm:max-w-lg") return maxWidthClassName;
    if (width === "md") return "sm:max-w-3xl";
    if (width === "lg") return "sm:max-w-5xl";
    return "sm:max-w-lg";
  }, [maxWidthClassName, width]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "p-0 gap-0 border-0 overflow-hidden flex flex-col",
          !isAndroidApp &&
            "max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]",
          resolvedMaxWidthClassName,
        )}
        containerClassName="z-111111111"
        hideCloseButton={forcePrimaryActionOnly}
        preventDismiss={forcePrimaryActionOnly}
      >
        <div
          id="status-modal-scroll-app"
          className={cn(
            "min-h-0 flex-1 overflow-y-auto p-4 text-center",
            className,
          )}
        >
          {/* Icon */}
          {config && IconComponent ? (
            <div className="flex justify-center mb-2">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className={cn(
                  "w-24 h-24 rounded-full bg-gradient-to-br flex items-center justify-center",
                  config.iconWrapperClass,
                )}
              >
                <div
                  className={cn(
                    "w-20 h-20 rounded-full bg-gradient-to-br flex items-center justify-center",
                    config.iconInnerClass,
                  )}
                >
                  <IconComponent className="w-10 h-10 text-white" />
                </div>
              </motion.div>
            </div>
          ) : null}

          {/* Title */}
          <h2
            className={cn(
              "text-2xl font-semibold text-foreground",
              config ? "mb-2" : "mb-4",
            )}
          >
            {title}
          </h2>

          {/* Message */}
          {message != null && (
            <div className="text-center plain-alert-message">
              {renderMessage(message)}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {customActions}
            {secondaryButtonLabel != null && onSecondaryAction != null && (
              <Button
                type="button"
                variant="outline"
                onClick={onSecondaryAction}
                className="rounded-full px-10 py-3 text-base touch-manipulation"
              >
                {secondaryButtonLabel}
              </Button>
            )}
            {primaryButtonLabel != null && onPrimaryAction != null && (
              <Button
                type="button"
                onClick={onPrimaryAction}
                // Use a non-filled variant so gradient styles always win.
                variant={variant === "error" ? "destructive" : "ghost"}
                className={cn(
                  variant === "error"
                    ? TAILWIND_BUTTON_BASE_CLASS
                    : TAILWIND_SUCCESS_BUTTON_RING_CLASS,
                  primaryButtonClassName,
                  "touch-manipulation",
                )}
              >
                {primaryButtonLabel}
              </Button>
            )}
          </div>
        </div>

        {/* Footer */}
        {footer != null && (
          <div className="border-t border-border py-4 px-6 text-center bg-muted/30">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatusModal;

/** Convenience component for error dialogs. Same as <StatusModal variant="error" ... /> */
export const ErrorModal: React.FC<Omit<StatusModalProps, "variant">> = (
  props,
) => <StatusModal {...props} variant="error" />;

/** Convenience component for success dialogs. Same as <StatusModal variant="success" ... /> */
export const SuccessModal: React.FC<Omit<StatusModalProps, "variant">> = (
  props,
) => <StatusModal {...props} variant="success" />;
